import React, {useEffect, useState} from 'react';
import {CommentaryType, dataService, PSSnapshotData} from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';
import {
    formatCommentary,
    formatDateLabel,
    getAvailableDates,
    getChange,
    getFirstDateWithPrelimCommentary,
    getSortedDates,
    normalizeRows,
    renderStatCard,
    shouldShowFinalByDefault,
    hasCommentaryData
} from './snapshotUtils';
import {commonChartOptions, createChart, formatLabel, regionColors, rsColors, getAxisColors} from './chartUtils';
import {Chart} from 'chart.js';
import {normalizeSnapshotResponse} from './snapshotDataUtils';
import {trackUserEvent} from "@/utils/userTracking";

interface SnapshotPSProps {
    data?: PSSnapshotData[];
    commentaryType?: 'preliminary' | 'final';
    onCommentaryTypeChange?: (type: 'preliminary' | 'final') => void;
    onSelectedDateChange?: (date: string) => void;
}

const SnapshotPS: React.FC<SnapshotPSProps> = ({ onCommentaryTypeChange, onSelectedDateChange }) => {
    const { isDark } = useTheme();
    const { tickColor, gridColor } = getAxisColors(isDark);
    const [stats, setStats] = useState<any>({});
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [commentaryTexts, setCommentaryTexts] = useState<any>({});
    const [commentaryType, setCommentaryType] = useState<CommentaryType>('preliminary');
    const [chartInstances, setChartInstances] = useState<{ [key: string]: Chart | null }>({});
    const [prelimData, setPrelimData] = useState<PSSnapshotData[]>([]);
    const [finalData, setFinalData] = useState<PSSnapshotData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const currentData: PSSnapshotData[] =
        commentaryType === 'final' && finalData.length > 0 ? finalData : prelimData;


    useEffect(() => {
        const loadBothDatasets = async () => {
            try {
                setLoading(true);
                const [prelimResp, finalResp] = await Promise.all([
                    dataService.getACCPSData(),
                    dataService.getACCPSDataFinal()
                ]);
                const prelimArray = normalizeSnapshotResponse<PSSnapshotData>(prelimResp) || [];
                const finalArray = normalizeSnapshotResponse<PSSnapshotData>(finalResp) || [];
                setPrelimData(prelimArray);
                setFinalData(finalArray);

                const base = finalArray.length > 0 ? finalArray : prelimArray;
                if (base.length > 0) {
                    initializeFromBase(base, finalArray, prelimArray);
                }
            } finally {
                setLoading(false);
            }
        };

        loadBothDatasets();
    }, []);

    const initializeFromBase = (rows: PSSnapshotData[], finalDataArray: PSSnapshotData[], prelimDataArray: PSSnapshotData[]) => {
        const normalizedRows = normalizeRows(rows);
        const sorted = getSortedDates(normalizedRows);
        const prelimDates = (prelimDataArray || []).map(d => d.date).filter(Boolean);

        const baseAvailable = getAvailableDates(sorted, 25, prelimDataArray);
        const available = baseAvailable.filter(date => prelimDates.includes(date));

        setAvailableDates(available);

        const firstDateWithCommentary = getFirstDateWithPrelimCommentary(prelimDataArray, 25);
        const defaultDate = firstDateWithCommentary || available[0] || sorted[0].date;

        const defaultRow = finalDataArray.find(f => f.date === defaultDate);
        const hasFinalCommentary = defaultRow && hasCommentaryData(defaultRow);
        const shouldUseFinal = hasFinalCommentary;

        setCommentaryType(shouldUseFinal ? 'final' : 'preliminary');

        setSelectedDate(defaultDate);
        onSelectedDateChange?.(defaultDate);
        updateDataForSelectedDate(defaultDate, normalizedRows);
    };

    useEffect(() => {
        if (!selectedDate || currentData.length === 0) return;
        const normalizedRows = normalizeRows(currentData);
        updateDataForSelectedDate(selectedDate, normalizedRows);
        setTimeout(() => {
            renderCharts(normalizedRows);
        }, 100);
    }, [selectedDate, commentaryType, prelimData, finalData, isDark]);

    const renderCharts = (rows: PSSnapshotData[]) => {

        Object.values(chartInstances).forEach(chart => {
            if (chart) {
                try {
                    chart.destroy();
                } catch {}
            }
        });


        const sortedData = [...rows].sort((a, b) => new Date(a.date) > new Date(b.date) ? 1 : -1);


        const selectedDateIndex = sortedData.findIndex(d => d.date === selectedDate);


        const startIndex12 = Math.max(0, selectedDateIndex - 12);
        const last12Months = sortedData.slice(startIndex12, selectedDateIndex + 1);
        const labels12Months = last12Months.map(d => formatLabel(d.date));



        const cumulative: number[] = [];
        let runningTotal = 0;

        last12Months.forEach((monthData, index) => {
            if (index === 0) {

                cumulative.push(0);
            } else {

                const change = Number(last12Months[index].chart_ps_inventory || 0);
                runningTotal += change;
                cumulative.push(runningTotal);
            }
        });

        const cumulativeCanvas = document.getElementById('psCumulativeChart') as HTMLCanvasElement;
        const cumulativeChart = createChart(cumulativeCanvas, {
            type: 'line',
            data: {
                labels: labels12Months,
                datasets: [{
                    label: 'PS Cumulative Draws/Gains',
                    data: cumulative,
                    borderColor: '#854cff',
                    backgroundColor: 'rgba(133,76,255,0.1)',
                    fill: false,
                    tension: 0.2,
                    pointRadius: 0
                }]
            },
            options: {
                ...commonChartOptions,
                plugins: {
                    legend: {display: false},
                    datalabels: {display: false}
                },
                scales: {
                    x: {ticks: {color: tickColor}, grid: {color: gridColor, lineWidth: 0.3}},
                    y: {
                        ticks: {color: tickColor},
                        grid: {color: gridColor, lineWidth: (ctx: any) => ctx.tick.value === 0 ? 2 : 0.3},
                        beginAtZero: true,
                        grace: '2%'
                    }
                }
            }
        });


        const totalSales = last12Months.map(d => +(d.chart_ps_sales || 0));
        const capacity = last12Months.map(d => +(d.chart_ps_capacity || 0));
        const production = last12Months.map(d => +(d.chart_ps_production || 0));

        const supplyDemandCanvas = document.getElementById('psSupplyDemand') as HTMLCanvasElement;
        const supplyDemandChart = createChart(supplyDemandCanvas, {
            type: 'bar',
            data: {
                labels: labels12Months,
                datasets: [
                    {
                        type: 'line' as const,
                        label: 'Capacity',
                        data: capacity,
                        borderColor: '#888888',
                        fill: false,
                        order: 3,
                        yAxisID: 'y',
                        pointRadius: 0
                    },
                    {
                        type: 'bar' as const,
                        label: 'Production',
                        data: production,
                        backgroundColor: '#42b200',
                        order: 1,
                        yAxisID: 'y',
                        categoryPercentage: 0.5,
                        barPercentage: 0.8
                    },
                    {
                        type: 'bar' as const,
                        label: 'Sales',
                        data: totalSales,
                        backgroundColor: '#854CFF',
                        order: 1,
                        yAxisID: 'y',
                        categoryPercentage: 0.5,
                        barPercentage: 0.8
                    }
                ]
            },
            options: {
                ...commonChartOptions,
                plugins: {
                    legend: {position: 'top' as const, labels: {color: tickColor}},
                    datalabels: {display: false}
                },
                scales: {
                    x: {stacked: false, ticks: {color: tickColor}, grid: {color: gridColor, lineWidth: 0.3}},
                    y: {ticks: {color: tickColor}, grid: {color: gridColor, lineWidth: 0.3}}
                }
            }
        });


        const createStackedChart = (canvasId: string, prefix: string) => {
            const fullPrefix = `chart_${prefix}_`;
            const keys = Object.keys(sortedData[0] || {}).filter(k => k.startsWith(fullPrefix));




            const startIndex = Math.max(0, selectedDateIndex - 1);
            const endIndex = Math.max(0, startIndex - 3);


            const chartData = sortedData.slice(endIndex, startIndex + 1);
            const labels4Months = chartData.map(d => formatLabel(d.date));

            const datasets = keys.map((key, idx) => {
                const friendlyLabel = key
                    .replace(fullPrefix, '')
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, c => c.toUpperCase());

                const values = chartData.map(row => {
                    const date = new Date(row.date);
                    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 2, 0).getDate();
                    return (+(row[key as keyof PSSnapshotData] || 0)) * daysInMonth;
                });

                return {
                    label: friendlyLabel,
                    data: values,
                    backgroundColor: regionColors[idx % regionColors.length],
                    stack: prefix,
                    yAxisID: 'y'
                };
            });

            const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
            return createChart(canvas, {
                type: 'bar',
                data: {
                    labels: labels4Months,
                    datasets
                },
                options: {
                    ...commonChartOptions,
                    plugins: {
                        legend: {
                            position: 'right' as const,
                            labels: {usePointStyle: true, pointStyle: 'circle', padding: 20, color: tickColor}
                        },
                        datalabels: {display: false}
                    },
                    scales: {
                        x: {stacked: true, ticks: {color: tickColor}, grid: {color: gridColor, lineWidth: 0.3}},
                        y: {stacked: true, ticks: {color: tickColor}, grid: {color: gridColor, lineWidth: 0.3}}
                    }
                }
            });
        };


        const smChart = createStackedChart('SMExports', 'sm');
        const bzChart = createStackedChart('BZImports', 'bz');
        const bdChart = createStackedChart('BDImports', 'bd');

        setChartInstances({
            cumulative: cumulativeChart,
            supplyDemand: supplyDemandChart,
            sm: smChart,
            bz: bzChart,
            bd: bdChart
        });
    };

    const updateDataForSelectedDate = (date: string, rows: PSSnapshotData[] = currentData) => {
        const sortedRows = [...rows].sort((a, b) => new Date(b.date) > new Date(a.date) ? 1 : -1);
        const latest = sortedRows.find(r => r.date === date);
        if (!latest) return;

        const currentIndex = sortedRows.findIndex(r => r.date === date);
        const prev = sortedRows[currentIndex + 1] || null;


        const calculate12MonthCumulative = (): number => {
            let cumulative = 0;
            const [year, month, day] = date.split('-');
            const yearAgoStr = `${Number(year) - 1}-${month}-${day}`;
            const yearAgoIndex = rows.findIndex(r => r.date === yearAgoStr);
            if (yearAgoIndex === -1) {
                return 0;
            }
            const window = rows.slice(currentIndex, yearAgoIndex + 1).reverse();
            for (let i = 0; i < window.length; i++) {
                const monthData = window[i];
                const inventoryChange = Number(monthData?.chart_ps_inventory || 0);
                cumulative += inventoryChange;
            }
            return cumulative;
        };

        const annual12MonthCumulative = calculate12MonthCumulative();


        const calculateSum = (prefix: string, data: any, daysInMonth: number): number => {
            let sum = 0;
            ['canada', 'china', 'europe', 'latin_america', 'mexico', 'other', 'pacific_rim'].forEach(region => {
                const value = data[`chart_${prefix}_${region}`] || 0;
                if (value) sum += parseFloat(value);
            });
            return sum * daysInMonth;
        };


        const getDaysInMonth = (dateString: string): number => {
            const dateObj = new Date(dateString);
            const year = dateObj.getFullYear();
            const month = dateObj.getMonth() + 2;
            return new Date(year, month, 0).getDate();
        };



        const smLatestData = sortedRows[currentIndex + 1] || latest;
        const bzLatestData = sortedRows[currentIndex + 1] || latest;
        const bdLatestData = sortedRows[currentIndex + 1] || latest;


        const currentDataDate = smLatestData.date;
        const currentDaysInMonth = getDaysInMonth(currentDataDate);
        const smSum = calculateSum('sm', smLatestData, currentDaysInMonth);
        const bzSum = calculateSum('bz', bzLatestData, currentDaysInMonth);
        const bdSum = calculateSum('bd', bdLatestData, currentDaysInMonth);

        let prevSmSum = 0;
        let prevBzSum = 0;
        let prevBdSum = 0;

        if (prev) {
            const smPrevData = rows[currentIndex + 2] || prev;
            const bzPrevData = rows[currentIndex + 2] || prev;
            const bdPrevData = rows[currentIndex + 2] || prev;


            const prevDataDate = smPrevData.date;
            const prevDaysInMonth = getDaysInMonth(prevDataDate);
            prevSmSum = calculateSum('sm', smPrevData, prevDaysInMonth);
            prevBzSum = calculateSum('bz', bzPrevData, prevDaysInMonth);
            prevBdSum = calculateSum('bd', bdPrevData, prevDaysInMonth);
        }

        const inventorChange = (diff: number) => {
            const formatted = Math.round(diff).toLocaleString();
            const sign = diff > 0 ? '+' : '';
            return `${sign}${formatted}`;
        }

        const statsData = {
            inventory: {
                value: annual12MonthCumulative.toLocaleString(),
                change: inventorChange(latest.chart_ps_inventory || 0),
            },

            demandRate: {
                value: Math.round((latest.DemandRates || latest.ps_demand_rates || 0) * 100).toString(),
                change: getChange((latest.DemandRates || latest.ps_demand_rates || 0) * 100, (prev?.DemandRates || prev?.ps_demand_rates || 0) * 100, true, 1, 1)
            },
            operatingRate: {
                value: Math.round((latest.OperatingRates || latest.ps_operating || 0) * 100).toString(),
                change: getChange((latest.OperatingRates || latest.ps_operating || 0) * 100, (prev?.OperatingRates || prev?.ps_operating || 0) * 100, true)
            },
            smSum: {
                value: smSum.toFixed(2),
                change: getChange(smSum, prevSmSum, false, 1, 2),
                previous: prevSmSum > 0 ? `(prev: ${prevSmSum.toFixed(2)})` : ''
            },
            bzSum: {
                value: bzSum.toFixed(2),
                change: getChange(bzSum, prevBzSum, false, 1, 2)
            },
            bdSum: {
                value: bdSum.toFixed(2),
                change: getChange(bdSum, prevBdSum, false, 1, 2)
            }
        };

        setStats(statsData);

        setCommentaryTexts(latest);
    };

    const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setSelectedDate(value);
        onSelectedDateChange?.(value);

        const finalRowForNewDate = finalData.find(f => f.date === value);
        const hasFinalCommentaryForNewDate = finalRowForNewDate && hasCommentaryData(finalRowForNewDate);

        if (commentaryType === 'final' && !hasFinalCommentaryForNewDate) {
            setCommentaryType('preliminary');
        }

        trackUserEvent("monthly_ps_snapshot_date_change", "PS Snapshot Date Changed", {
            date: value
        });
    };

    const selectedDateHasFinalCommentary = () => {
        const finalRow = finalData.find(f => f.date === selectedDate);
        if (!finalRow) return false;

        const selectedDateObj = new Date(selectedDate);
        const now = new Date();
        const monthsDiff = (now.getFullYear() - selectedDateObj.getFullYear()) * 12 + (now.getMonth() - selectedDateObj.getMonth());

        if (monthsDiff >= 2) {
            return true;
        }

        return hasCommentaryData(finalRow);
    };

    if (loading) {
        return (
            <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
        );
    }

    return (
        <>
            <div className="flex justify-between items-center mb-5">
                <div className="flex items-center gap-4">
                    <select
                        value={selectedDate}
                        onChange={handleDateChange}
                        className="border-rs-jet border-2 p-4 rounded-lg"
                    >
                        {availableDates.map(date => (
                            <option key={date} value={date}>
                                {formatDateLabel(date)}
                            </option>
                        ))}
                    </select>
                    <select
                        value={commentaryType}
                        onChange={(e) => setCommentaryType(e.target.value as CommentaryType)}
                        className="border-rs-jet border-2 p-4 rounded-lg"
                    >
                        <option value="preliminary">Preliminary</option>
                        {selectedDateHasFinalCommentary() && <option value="final">Final</option>}
                    </select>
                </div>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                {renderStatCard('YOY Cumulative Inventory (MM/lbs)', stats.inventory?.value || '0', stats.inventory?.change || '0', '', '', '')}
                {renderStatCard('Demand Rate', `${stats.demandRate?.value || '0'}%`, stats.demandRate?.change || '0', '', '', '')}
                {renderStatCard('Operating Rate', `${stats.operatingRate?.value || '0'}%`, stats.operatingRate?.change || '0', '', '', '')}
            </div>

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-rs-off-black rounded-lg p-5">
                    <h4 className="text-center mb-3 font-medium">PS Cumulative Draws/Gains (MM/lbs)</h4>
                    <div className="h-[380px]">
                        <canvas id="psCumulativeChart" className="w-full h-full"></canvas>
                    </div>
                    <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
                        <p
                            className="whitespace-pre-line text-base leading-relaxed"
                            dangerouslySetInnerHTML={{__html: formatCommentary(commentaryTexts.ps_inv_cum_gains_draws)}}
                        />
                    </div>
                </div>

                <div className="bg-rs-off-black rounded-lg p-5">
                    <h4 className="text-center mb-3 font-medium">PS Supply and Demand (MM/lbs)</h4>
                    <div className="h-[380px]">
                        <canvas id="psSupplyDemand" className="w-full h-full"></canvas>
                    </div>
                    <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
                        <p
                            className="whitespace-pre-line text-base leading-relaxed"
                            dangerouslySetInnerHTML={{__html: formatCommentary(commentaryTexts.ps_supply_demand)}}
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-10">
                {renderStatCard('SM Export Volume (MM/lbs)', stats.smSum?.value || '0', stats.smSum?.change || '0')}
                {renderStatCard('BZ Import Volume (MM/gals)', stats.bzSum?.value || '0', stats.bzSum?.change || '0')}
                {renderStatCard('BD Import Volume (MM/lbs)', stats.bdSum?.value || '0', stats.bdSum?.change || '0')}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-h-[500px]">
                <div className="bg-rs-off-black rounded-lg p-5">
                    <h4 className="text-center mb-3 font-medium">Styrene Exports (MM/lbs)</h4>
                    <div className="h-[380px]">
                        <canvas id="SMExports" className="w-full h-full"></canvas>
                    </div>
                    <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
                        <p
                            className="whitespace-pre-line text-base leading-relaxed"
                            dangerouslySetInnerHTML={{__html: formatCommentary(commentaryTexts.ps_styrene_exports)}}
                        />
                    </div>
                </div>

                <div className="bg-rs-off-black rounded-lg p-5">
                    <h4 className="text-center mb-3 font-medium">Benzene Imports (MM/gals)</h4>
                    <div className="h-[380px]">
                        <canvas id="BZImports" className="w-full h-full"></canvas>
                    </div>
                    <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
                        <p
                            className="whitespace-pre-line text-base leading-relaxed"
                            dangerouslySetInnerHTML={{__html: formatCommentary(commentaryTexts.ps_benzene_imports)}}
                        />
                    </div>
                </div>

                <div className="bg-rs-off-black rounded-lg p-5">
                    <h4 className="text-center mb-3 font-medium">Butadiene Imports (MM/lbs)</h4>
                    <div className="h-[380px]">
                        <canvas id="BDImports" className="w-full h-full"></canvas>
                    </div>
                    <div className="bg-rs-off-black rounded-lg p-7 flex items-center">
                        <p
                            className="whitespace-pre-line text-base leading-relaxed"
                            dangerouslySetInnerHTML={{__html: formatCommentary(commentaryTexts.ps_butadiene_imports)}}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default SnapshotPS;
